import { ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react'
import { senderNeedsConfirming } from '@/app/document-review/approval-items'
import { REVIEW_INSTANT_FORMAT } from '@/app/document-review/review-labels'
import type { DocumentReviewDetailOutput } from '@/app/document-review/use-document-review'

type ReviewEmail = NonNullable<DocumentReviewDetailOutput['email']>

function senderWarning(email: ReviewEmail): string | null {
  if (!senderNeedsConfirming(email)) return null
  const trader = email.senderDomainTrader
  if (email.verification === 'failed') {
    return trader === null
      ? 'Signature check failed. The email may have been altered or forged.'
      : `Signature check failed for ${email.senderDomain}, ${trader}'s domain. Confirm with ${trader} before assigning.`
  }
  if (trader === null) return null
  return `Not signed by ${email.senderDomain}, ${trader}'s domain. Confirm with ${trader} before assigning.`
}

function SenderCheck({ email }: { email: ReviewEmail }) {
  if (email.verification === 'verified') {
    return (
      <span className="mt-0.5 flex items-center gap-1">
        <ShieldCheck className="h-3.5 w-3.5 text-green-600 dark:text-green-400" aria-hidden />
        Signed by {email.verifiedSenderDomain}
      </span>
    )
  }
  const warning = senderWarning(email)
  if (warning !== null) {
    return (
      <span className="mt-0.5 flex items-start gap-1 text-amber-700 dark:text-amber-400" role="alert">
        <ShieldX className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
        {warning}
      </span>
    )
  }
  return (
    <span className="mt-0.5 flex items-center gap-1">
      <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
      {email.verification === 'unchecked' ? 'Signature not checked' : 'Sender not verified'}
    </span>
  )
}

function EmailProvenance({ email }: { email: ReviewEmail }) {
  return (
    <footer className="text-muted-foreground border-t px-3 py-2 text-xs">
      <span>Emailed by </span>
      <span className="text-foreground break-all">{email.sender}</span>
      {email.receivedAt !== null && <span> on {email.receivedAt.toString(REVIEW_INSTANT_FORMAT)}</span>}
      <SenderCheck email={email} />
    </footer>
  )
}

type Arrival = Pick<DocumentReviewDetailOutput, 'email' | 'sentBy' | 'arrivedAt'>

export function ArrivalProvenance({ arrival }: { arrival: Arrival }) {
  if (arrival.email !== null) return <EmailProvenance email={arrival.email} />
  return <UploadProvenance sentBy={arrival.sentBy} arrivedAt={arrival.arrivedAt} />
}

function UploadProvenance({ sentBy, arrivedAt }: Omit<Arrival, 'email'>) {
  return (
    <footer className="text-muted-foreground border-t px-3 py-2 text-xs">
      <span>Uploaded by </span>
      <span className="text-foreground">{sentBy ?? 'an unknown user'}</span>
      {arrivedAt !== null && <span> on {arrivedAt.toString(REVIEW_INSTANT_FORMAT)}</span>}
    </footer>
  )
}
